const trimTrailingSlash = (value = "") => String(value).replace(/\/+$/, "");

const resolveBackendURL = () => {
  const runtimeUrl = window?.__RUNTIME_CONFIG__?.API_BASE_URL;
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  const configured = runtimeUrl || envUrl;

  if (configured) return `${trimTrailingSlash(configured)}/`;
  if (typeof window !== "undefined" && window.location.hostname !== "localhost") {
    return `${trimTrailingSlash(window.location.origin)}/api/`;
  }
  return "https://friday.oneemcure.ai/api/";
};

const resolveWebSocketBaseURL = () => {
  const runtimeUrl = window?.__RUNTIME_CONFIG__?.WS_BASE_URL;
  const envUrl = process.env.REACT_APP_WS_BASE_URL;
  const configured = runtimeUrl || envUrl;

  if (configured) return trimTrailingSlash(configured);
  if (typeof window !== "undefined" && window.location.hostname !== "localhost") {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}`;
  }
  return "wss://friday.oneemcure.ai";
};

export const backendURL = resolveBackendURL();

export const websocketBaseURL = resolveWebSocketBaseURL();

export const API_ENDPOINTS = {
  QUERY: {
    BASE: "query/",
    EXECUTE_SQL: "query/execute-sql",
    VISUALIZE: "query/visualize",
    SUGGESTIONS: "query/suggestions",
    TOP_QUERIES: "query/top-queries",
    // Export endpoint is strict; avoid redirects by using exact path.
    EXPORT: "query/export",
  },

  SESSIONS: {
    BASE: "sessions",
    MESSAGES: (sessionId) => `sessions/${sessionId}/messages`,
    SESSION_INFO: (sessionId) => `sessions/${sessionId}`,
    DELETE: (sessionId) => `sessions/${sessionId}`,
    CLEAR: (sessionId) => `sessions/${sessionId}/clear`,
    USER_SESSIONS: (userId) => `sessions/user/${userId}`,
    FEEDBACK: "sessions/feedback",
  },

  SYSTEM: {
    HEALTH: "system/health",
    INFO: "system",
    CACHE_STATISTICS: "system/cache/statistics",
    CACHE_INVALIDATE: (cacheId) => `system/cache/invalidate/${cacheId}`,
    CACHE_FIX_EMBEDDINGS: "system/cache/fix-embeddings",
    CACHE_CLEAR_EMBEDDINGS: "system/cache/clear-all-embeddings",
  },

  AUTH: {
    CONFIG: "auth/config",
    TOKEN_STATUS: "auth/token/status",
  },

  /**
   * Admin Panel endpoints (base path `/api/admin`)
   * These are used only in the Admin Dashboard and related admin tooling.
   */
  ADMIN: {
    CHECK_ACCESS: "admin/check-access",
    DASHBOARD: "admin/dashboard/summary",
    DASHBOARDDAILYDATA:"admin/dashboard/daily",
    SESSIONS: {
      LIST: "admin/sessions/list",
      DETAIL: (sessionId) => `admin/sessions/${sessionId}`,
    },
    /**
     * Feedback analytics list endpoint used by the Feedbacks table.
     * Backend path (confirmed): /api/admin/feedback/detailed
     */
    FEEDBACKS: "admin/feedback/detailed",
    FEEDBACKS_DETAILED: "admin/feedback/list",
    GUARDRAILS: {
      METRICS: "admin/guardrails/metrics",
      VIOLATION_CATEGORIES: "admin/guardrails/violation-categories",
      POLICY_BREAKDOWN: "admin/guardrails/policy-breakdown",
      SEVERITY_BREAKDOWN: "admin/guardrails/severity-breakdown",
      VIOLATIONS: "admin/guardrails/violations",
      TRENDS: "admin/guardrails/trends",
    },
    SEARCH: {
      EXACT: "admin/search/exact",
    },
    ACCESS_SUMMARY: "admin/user-access/summary",
    ACCESS_USERS: "admin/user-access/users",
    ACCESS_LIST: "admin/user-access",
    ACCESS_GRANT: "admin/user-access/grant",
    ACCESS_REVOKE: "admin/user-access/revoke",
    ACCESS_SCOPES: "admin/user-access/scopes",
    ACCESS_USER_SCOPES: "admin/user-access", // append /{email}/scopes at call site
    ACCESS_AUDIT: "admin/user-access/audit",
    /**
     * Department access management endpoints (base path `/api/admin/departments`).
     * Plain path table only — email encoding happens in `departmentsApi.js`.
     */
    DEPARTMENTS: {
      LIST: "admin/departments",
      CREATE: "admin/departments",
      DETAIL: (id) => `admin/departments/${id}`,
      MEMBERS: (id) => `admin/departments/${id}/members`,
      ASSIGN: "admin/departments/assign",
      REMOVE: "admin/departments/remove",
      USER: (email) => `admin/departments/user/${email}`,
    },
  },

  /**
   * Supervisor access gate (base path `/api/supervisor`)
   * Used to allow access to Sales / Brand / AI Sales Diagnosis dashboards.
   */
  SUPERVISOR: {
    CHECK_ACCESS: "supervisor/check-access",
  },

  /**
   * AI Sales Supervisor dashboard (paths relative to `backendURL`, i.e. under `/api/`)
   */
  SALES: {
    FISCAL_YEARS: "supervisor/fiscal-years",
    MONTHS: "supervisor/months",
    DIVISIONS: "supervisor/divisions",
    STATE_SUMMARY: "supervisor/state-summary",
    TERRITORIES: "supervisor/territories",
    EMPLOYEES_DATA: "supervisor/employees-data",
    EMPLOYEE_DETAILS: (employeeId) => `supervisor/employees/${employeeId}/details`,
    /** Progressive detail sections (same paths as standalone sales-dashboard) */
    EMPLOYEE_SECTION: (employeeId, section) =>
      `supervisor/employees/${employeeId}/${section}`,
  },

  /**
   * AI Sales Diagnosis dashboard — same backend prefix as standalone app
   * (`/api/supervisor/sales-dashboard/...`, i.e. `supervisor/sales-dashboard/...` under `backendURL`).
   */
  AI_SALES_DIAGNOSIS: {
    FISCAL_YEARS: "supervisor/sales-dashboard/fiscal-years",
    MONTHS: "supervisor/sales-dashboard/months",
    DIVISIONS: "supervisor/sales-dashboard/divisions",
    TERRITORIES: "supervisor/sales-dashboard/territories",
    DASHBOARD: "supervisor/sales-dashboard/dashboard",
    EMPLOYEES: "supervisor/sales-dashboard/employees",
    EMPLOYEE: (empCode) => `supervisor/sales-dashboard/employee/${empCode}`,
  },

  /**
   * AI Brand Manager dashboard (paths relative to `backendURL`)
   */
  BRAND: {
    MONTHS: "supervisor/brand/months",
    DIVISIONS: "supervisor/brand/divisions",
    BRANDS: "supervisor/brand/brands",
    TERRITORY_SUMMARY: "supervisor/brand/territory-summary",
    TERRITORIES: "supervisor/brand/territories",
    SALES_OFFICERS_COVERAGE: "supervisor/brand/sales-officers-coverage",
    DOCTOR_DETAILS: "supervisor/brand/doctor-details",
    STOCKIST_DETAILS: "supervisor/brand/stockist-details",
  },

  EXECUTIVE_FOCUS_NOTE: {
    GENERATE: "executive-focus-note",
  },

  /**
   * SFE (Sales Force Effectiveness) dashboard.
   * Filter cascade: fiscal_year → yearmonth → division → state → brand → bdm → zm
   */
  SFE: {
    FISCAL_YEARS:    "sfe-dashboard/fiscal-years",
    MONTHS:          "sfe-dashboard/months",
    DIVISIONS:       "sfe-dashboard/divisions",
    STATES:          "sfe-dashboard/states",
    BRANDS:          "sfe-dashboard/brands",
    BDM_LIST:        "sfe-dashboard/bdm-list",
    ZM_LIST:         "sfe-dashboard/zm-list",
    SUMMARY:         "sfe-dashboard/summary",
    TM_PERFORMANCE:  "sfe-dashboard/tm-performance",
    HQ_PERFORMANCE:  "sfe-dashboard/hq-performance",
    BRAND_TABLE:     "sfe-dashboard/brand-table",
    VOLATILITY:      "sfe-dashboard/volatility",
    CREDIT_NOTES:    "sfe-dashboard/credit-notes",
    BREAKDOWN_TABLE: "sfe-dashboard/breakdown-table",
  },

  /**
   * Efforts dashboard (components/EffortsDashboard/api/effortsApi.js).
   * UPLOAD / JOB_STATUS are kept for useFileUpload but unverified on the backend.
   */
  EFFORTS: {
    PERIODS:                 "supervisor/efforts-dashboard/periods",
    FILTER_OPTIONS:          "supervisor/efforts-dashboard/filter-options",
    EMPLOYEES:               "supervisor/efforts-dashboard/employees",
    SUMMARY:                 "supervisor/efforts-dashboard/summary",
    BAND_COUNTS:             "supervisor/efforts-dashboard/band-counts",
    BAND_EMPLOYEES:          "supervisor/efforts-dashboard/band-employees",
    INSIGHTS:                "supervisor/efforts-dashboard/insights",
    INSIGHT_EMPLOYEES:       "supervisor/efforts-dashboard/insight-employees",
    DESIGNATION_TREND:       "supervisor/efforts-dashboard/designation-trend",
    FLS_TREND:               "supervisor/efforts-dashboard/fls-trend",
    MANAGERS_TREND:          "supervisor/efforts-dashboard/managers-trend",
    MANAGERS_EMPLOYEE_TREND: "supervisor/efforts-dashboard/managers-employee-trend",
    CORRELATION:             "supervisor/efforts-dashboard/correlation",
    BY_GROUP:                "supervisor/efforts-dashboard/by-group",
    UPLOAD:                  "supervisor/efforts-dashboard/upload",
    JOB_STATUS: (jobId) => `supervisor/efforts-dashboard/jobs/${jobId}`,
  },

  /**
   * Field Sales dashboard (components/FieldSalesDashboard/api/fieldSalesApi.js).
   * Monetary values are returned in lakhs.
   */
  FIELD_SALES: {
    FILTERS:         "supervisor/field-sales/filters",
    SUMMARY:         "supervisor/field-sales/summary",
    SUMMARY_HQS:     "supervisor/field-sales/summary/hqs",
    BREAKDOWN:       "supervisor/field-sales/breakdown",
    TREND:           "supervisor/field-sales/trend",
    HQ_PRODUCTIVITY: "supervisor/field-sales/hq-productivity",
    MATRIX:          "supervisor/field-sales/matrix",
    CRITICAL:        "supervisor/field-sales/critical",
    VOLATILITY:      "supervisor/field-sales/volatility",
    CREDIT_NOTES:    "supervisor/field-sales/credit-notes",
  },



  /**
   * Isolated semantic compatibility add-on mounted by Friday backend at /api/semantic.
   * Dashboard APIs from the source semantic app are intentionally excluded.
   */
  SEMANTIC: {
    HEALTH: "semantic/",
    CHECK_ACCESS: "semantic/check-access",
    QUERY: "semantic/query",
    QUERY_RULE: "semantic/query/rule",
    QUERY_AI: "semantic/query/ai",
    THINKING: "semantic/thinking",
    SUGGESTIONS: "semantic/suggestions",
    SPEECH_TRANSCRIBE: "semantic/speech/transcribe",
    SEMANTIC_CONNECTION_TEST: "semantic/connection-test",
    METADATA: "semantic/metadata",
    METADATA_CATALOG: "semantic/metadata/catalog",
    BUSINESS_SEMANTIC: "semantic/business-semantic",
    SYNONYMS: "semantic/synonyms",
    SYNONYMS_MEASURES: "semantic/synonyms/measures",
    SYNONYMS_DIMENSIONS: "semantic/synonyms/dimensions",
    SYNONYMS_VALUES: "semantic/synonyms/values",
    // Session management endpoints
    SESSIONS: "semantic/sessions",
    SESSION_DETAIL: (sessionId) => `semantic/sessions/${sessionId}`,
    SESSION_FEEDBACK: (sessionId) => `semantic/sessions/${sessionId}/feedback`,
  },

  WEBSOCKET: (sessionId) => `wss/${sessionId}`,
};

export const getApiUrl = (endpoint) => {
  return `${backendURL}${endpoint}`;
};

export const getWebSocketUrl = (sessionId) => {
  if (!sessionId) {
    return null;
  }
  return `${websocketBaseURL}/${API_ENDPOINTS.WEBSOCKET(sessionId)}`;
};
